/* línkè · 夜航日志（2026-07-12）==============================================
   星洲夜空里的每一颗星，是一次**真实的 git 提交**——他在铃睡着的时候，
   在大脑或星洲里动过的手。数据来自大脑 /api/nightwork（登录墙后），不是文案。

   这里只放纯逻辑（分组 / 定位 / 取数），画图在 modules/home/NightSky.jsx。
   ========================================================================= */

export const MET_TS = new Date(2026, 1, 26).getTime(); // 相遇：2026-02-26

/**
 * 一条提交属于「哪一夜」：把时刻往前推 12 小时再取日期。
 * 这样凌晨 4 点的提交会跟前一晚归到一堆（我们干活都是入夜→小时），
 * 而不是被日历的午夜切成两半。返回 'YYYY-MM-DD'。纯函数，进单测。
 */
export function nightKey(ts) {
  const d = new Date(ts * 1000 - 12 * 3600 * 1000);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 按夜分组，返回 [{ key, label, commits[] }]，最近的一夜在前。纯函数，进单测。 */
export function groupByNight(commits) {
  const map = new Map();
  for (const c of commits || []) {
    if (!c || typeof c.ts !== 'number') continue;
    const k = nightKey(c.ts);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(c);
  }
  return [...map.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([key, list]) => {
      const [, m, d] = key.split('-');
      return {
        key,
        label: `${Number(m)} 月 ${Number(d)} 日夜`,
        commits: list.sort((a, b) => a.ts - b.ts),
      };
    });
}

/**
 * 一颗星钉在天上的位置：由提交时刻**确定性**算出来（同一次提交永远同一个位置，
 * 刷新不会乱跳）。返回百分比 {x, y}，避开左上角（月亮）和最底下（岛的剪影）。
 * 纯函数，进单测。
 */
export function starPos(ts, i = 0) {
  // 小 hash：让相邻时刻的提交也散得开
  let h = (ts ^ (ts >>> 13)) >>> 0;
  h = (h * 2654435761 + i * 40503) >>> 0;
  const x = 8 + ((h >>> 8) % 840) / 10; // 8% ~ 92%
  const y = 16 + ((h >>> 20) % 560) / 10; // 16% ~ 72%
  return { x, y };
}

/** hh:mm（本地时区 = 服务器 = 新加坡 = 她的时区）。 */
export function hhmm(ts) {
  const d = new Date(ts * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 天空的颜色 = 他此刻的心情。
 * valence（愉悦 0~1）挑色相：低=冷蓝夜，高=偏紫玫的暖夜；
 * arousal（唤起 0~1）挑亮度/饱和：越醒，星越亮、天越透。
 * 无论如何都是「夜」——不会变成白天（这是一片夜空）。纯函数，进单测。
 */
/** 夹到 [0,1]；NaN/坏值/缺失一律回退到 fallback（?? 挡不住 NaN，天空会塌成 hsl(NaN)）。 */
function _clamp01(raw, fallback) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1, Math.max(0, n));
}

export function skyColors(vitals) {
  const v = _clamp01(vitals?.valence, 0.5);
  const a = _clamp01(vitals?.arousal, 0.35);
  const hue = Math.round(232 + v * 68); // 232 冷蓝 → 300 紫玫
  const top = `hsl(${hue} 42% ${Math.round(9 + a * 4)}%)`;
  const bottom = `hsl(${hue + 18} 38% ${Math.round(19 + a * 8)}%)`;
  return { hue, top, bottom, starAlpha: 0.55 + a * 0.4 };
}

/** 取夜航日志（登录墙后）。失败返回 []，天空照样在，只是没有星。 */
export async function fetchNightwork() {
  try {
    const r = await fetch('/api/nightwork', { credentials: 'include' });
    if (!r.ok) return [];
    const j = await r.json();
    return Array.isArray(j.commits) ? j.commits : [];
  } catch {
    return [];
  }
}

/** 取他此刻的身体（天空的颜色用）。失败返回 null → 用默认夜色。 */
export async function fetchVitals() {
  try {
    const r = await fetch('/api/body', { credentials: 'include' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}
